// **The tournament chapters, mounted in place** (`demo-presentation-shell` task 12.2, design
// **DD9**, plan **D-4**). The `nav` cue's three targets are `tournament-big-screen`'s own shipped
// components (contract **C-3**), mounted **inside** the demo root — never navigated to — so the
// fullscreen state, the keyboard map and the sequencer cursor all survive the hand-off, which is
// the script's "seamlessly".
//
// **How "state is held above the mount" is realised.** DD9 asks that advancing away from a
// tournament chapter and back show it with its state intact rather than remounted empty. The three
// C-3 components hold their own poll/playhead state internally (`HostGathering`'s members poll,
// `HostLeaderboard`'s leaderboard poll, `SpectateView`'s playhead) — they are shipped components
// this change composes rather than redesigns, so there is no prop to hoist that state into. What is
// held above the cursor instead is **the mount itself**: a chapter is mounted the first time its
// cue is reached and stays mounted for the rest of the show, hidden rather than unmounted when the
// cursor is elsewhere. That is strictly stronger than hoisting a state object — the poll keeps
// running and the playhead keeps its position — and it costs one `display` toggle.
//
// A chapter is **not** mounted before its cue is first reached: a members poll running from the
// opening slide would be a request per three seconds for the whole first half of the show.

import { useRef } from 'react';
import type { CSSProperties, ReactElement } from 'react';
import type { DemoNavTarget, TournamentChapter } from './cues';
import { HostGathering } from '../../ui/screens/HostGathering';
import { HostLeaderboard } from '../../ui/screens/HostLeaderboard';
import { SpectateView, type SpectateViewProps } from '../../ui/screens/SpectateView';
import type { HostTournamentTransport } from '../../net/rounds-host-http';
import { joinUrl, type QrEncoder } from '../rounds/qr-join';
import { Z } from './z-layers';

/**
 * Everything the three C-3 components are forbidden to construct for themselves, supplied by the
 * demo's own composition root (`DemoRoot.tsx`) from the secret-custody flow's host membership
 * credential.
 *
 * Every field is optional for the same reason `DemoAppProps.createStageDriver` has no default: a
 * bare mount, and every test that does not care about the tournament, must render inertly rather
 * than reach for a credential that is not there.
 */
export interface DemoTournamentSeams {
  /** Host-only reads and the Start transition, over the host membership's bearer. */
  transport?: HostTournamentTransport;
  /** The plaintext join code, from the create-and-host response — its only delivery. */
  joinCode?: string;
  /** The origin a projected QR points at — the room's own reachable address. */
  origin?: string;
  encodeQr?: QrEncoder;
  /** Moves the leaderboard on to the battle-royale playback. The demo's own advance key is the
   * usual way there; this is the on-screen control the presenter can press instead. Supplied by
   * `DemoApp` only once the `nextButton` cue has fired — its presence *is* the control's, since
   * `HostLeaderboard` renders the button only when it has somewhere to go. */
  onWatch?: () => void;
  /** Whether the winner's prompts beat has been reached (`winnersPrompts`). Defaults to
   * `HostLeaderboard`'s own `true`; the demo passes `false` until its cue, so the reveal is a beat
   * the presenter narrates rather than something the poll springs on the room. */
  showWinnerPrompts?: boolean;
  /** The spectate chapter's trace reader, subject, roster and phase. Absent renders the projected
   * chrome with no trace open, exactly as a bare `RoundsReview` mount does. */
  spectate?: SpectateViewProps;
  /**
   * **The awaiting-start beat has a state of its own** (E2E round-2 finding N-4, 2026-08-09).
   *
   * Three consecutive `nav` cues target this same chapter (`cues.ts`'s `nav-qr-gathering`,
   * `nav-tournament-start`, `nav-awaiting-start`), and advancing across the second changed nothing
   * a room could see: pressing Start *is* the presenter's own click on `HostGathering`'s button,
   * which already flips its own label the instant it lands — so distinguishing that cue from the
   * one before it would be inventing a difference where the script has none. The third cue is
   * different: the script's own words are "players' screens show 'awaiting start'", a real state
   * change the projector has to mirror. This seam is that mirror — `false`/absent until `DemoApp`
   * sets it once the `nav-awaiting-start` cue is reached, swapping the join surface for the
   * arena-filling interstitial below rather than continuing to invite scans the room can no longer
   * act on (membership closed the moment Start was pressed).
   */
  awaitingStart?: boolean;
}

export interface DemoTournamentChaptersProps {
  /** The `nav` cue's target at the cursor, or `null` when the cursor is not on a `nav` cue. */
  target: DemoNavTarget | null;
  seams?: DemoTournamentSeams;
}

/** Reconciles this change's `nav` targets (`cues.ts`, named before C-3 landed) with C-3's real
 * component names — task 12.5's "swap the stubs for the real components". */
const COMPONENT_OF: Readonly<Record<TournamentChapter, 'HostGathering' | 'HostLeaderboard' | 'SpectateView'>> = {
  gathering: 'HostGathering',
  leaderboard: 'HostLeaderboard',
  playback: 'SpectateView',
};

function renderChapter(chapter: TournamentChapter, seams: DemoTournamentSeams): ReactElement {
  switch (chapter) {
    case 'gathering':
      return seams.transport === undefined ? (
        <div data-testid="demo-tournament-pending" role="status" />
      ) : (
        <>
          {/* Hidden, never unmounted, once the arena-filling beat is reached — the same DD9 rule
              `DemoTournamentChapters` applies to a whole chapter, applied one level down: reversing
              the cue back to `nav-qr-gathering`/`nav-tournament-start` shows the join surface again
              with its polled member list intact rather than remounting it empty. */}
          <div style={seams.awaitingStart === true ? { display: 'none' } : undefined}>
            <ProjectedGathering
              origin={seams.origin ?? ''}
              joinCode={seams.joinCode ?? ''}
            >
              <HostGathering
                transport={seams.transport}
                joinCode={seams.joinCode ?? ''}
                origin={seams.origin ?? ''}
                encodeQr={seams.encodeQr ?? (() => Promise.resolve(''))}
              />
            </ProjectedGathering>
          </div>
          {seams.awaitingStart === true && <AwaitingStartInterstitial />}
        </>
      );
    case 'leaderboard':
      return seams.transport === undefined ? (
        <div data-testid="demo-tournament-pending" role="status" />
      ) : (
        <>
          <HostLeaderboard
            transport={seams.transport}
            onWatch={seams.onWatch}
            showWinnerPrompts={seams.showWinnerPrompts ?? false}
          />
          <a
            data-testid="demo-start-playoff"
            href="/?mode=live&pvp=true&opponent=human"
            target="_blank"
            rel="noreferrer"
            style={PLAYOFF_LINK_STYLE}
          >
            Start the two-player playoff
          </a>
        </>
      );
    case 'playback':
      return <SpectateView {...(seams.spectate ?? {})} />;
  }
}

const PLAYOFF_LINK_STYLE: CSSProperties = {
  position: 'fixed',
  right: 32,
  bottom: 28,
  zIndex: Z.playoffLink,
  padding: '13px 20px',
  borderRadius: 999,
  background: 'var(--accent-strong)',
  color: '#fffdf6',
  fontFamily: 'var(--font-display)',
  fontWeight: 600,
  textDecoration: 'none',
};

/**
 * **The paper ground a projected tournament chapter stands on** (E2E finding M11).
 *
 * The three C-3 components are composable by design: they paint no background, because in their own
 * app they are mounted inside the Field Journal shell that does. `/demo` mounts them over the stage
 * canvas region instead, which is dark — so the join code, the "n / 150 joined" line, the preview
 * readiness and the joiner names all rendered ink-on-black and were, on a projector, simply not
 * there. The demo's composition supplies the ground its own composition removed, from the kit's own
 * tokens; the components are untouched.
 *
 * `position: fixed` rather than a wrapper's flow box: this whole subtree sits beside an
 * `inset: 0` stage layer, so a chapter that only sized itself to its content would still leave the
 * dark canvas showing around it.
 */
const CHAPTER_SURFACE_STYLE: CSSProperties = {
  position: 'fixed',
  inset: 0,
  overflow: 'auto',
  background: 'var(--surface-app)',
  color: 'var(--text-body)',
  fontFamily: 'var(--font-body)',
};

/**
 * **The QR beat, laid out for a projector** (E2E round-3 polish).
 *
 * `HostGathering` is a phone-and-laptop component: it stacks its QR, code, count, names and Start
 * in a 24 px-padded column from the top of whatever box it is given. Given a 1920 × 1080 one it
 * filled the top ~40% and left the rest of the wall blank while a room full of people looked at it.
 *
 * The component is untouched — the fix is the box. It is centred rather than top-stacked, and the
 * space that buys is spent on the **join URL in full**, large: the one thing on this screen an
 * audience member who cannot scan (a laptop, a locked-down camera, a bad angle) actually needs, and
 * the exact string the QR beside it already encodes. Nothing here is invented copy.
 */
function ProjectedGathering({
  origin,
  joinCode,
  children,
}: {
  origin: string;
  joinCode: string;
  children: ReactElement;
}): ReactElement {
  const url = joinUrl(origin, joinCode);
  return (
    <div data-testid="demo-gathering-projection" style={GATHERING_PROJECTION_STYLE}>
      {children}
      {joinCode.length > 0 && (
        <p data-testid="demo-gathering-join-url" style={GATHERING_URL_STYLE}>
          {url}
        </p>
      )}
    </div>
  );
}

const GATHERING_PROJECTION_STYLE: CSSProperties = {
  minHeight: '100%',
  boxSizing: 'border-box',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 'var(--sp-6, 24px)',
  padding: 'var(--sp-8, 32px)',
  textAlign: 'center',
};

const GATHERING_URL_STYLE: CSSProperties = {
  margin: 0,
  fontFamily: 'var(--font-mono)',
  fontSize: 'clamp(24px, 3.2vw, 56px)',
  letterSpacing: 2,
  wordBreak: 'break-all',
  color: 'var(--ink-1)',
};

/**
 * **The arena-filling beat** (E2E round-2 finding N-4, 2026-08-09) — the projector's mirror of the
 * script's own "players' screens show 'awaiting start'". Membership closed the moment Start was
 * pressed, so the join surface's QR and code are no longer actionable; this replaces them rather
 * than leaving an invitation on screen nobody can still take.
 */
function AwaitingStartInterstitial(): ReactElement {
  return (
    <div data-testid="demo-gathering-awaiting-start" style={GATHERING_PROJECTION_STYLE}>
      <p style={AWAITING_START_HEADLINE_STYLE}>Arena filling — awaiting start</p>
    </div>
  );
}

const AWAITING_START_HEADLINE_STYLE: CSSProperties = {
  margin: 0,
  fontFamily: 'var(--font-display)',
  fontWeight: 600,
  fontSize: 'clamp(28px, 4vw, 64px)',
  color: 'var(--ink-1)',
};

export function DemoTournamentChapters({
  target,
  seams = {},
}: DemoTournamentChaptersProps): ReactElement | null {
  // Insertion-ordered, so the render order below is the order the chapters were first reached.
  // A ref rather than state: adding to it during render and reading it in the same pass needs no
  // second render, and nothing outside this component observes it.
  const shown = useRef<TournamentChapter[]>([]);
  const tournamentTarget = target !== null && target in COMPONENT_OF ? target as TournamentChapter : null;
  if (tournamentTarget !== null && !shown.current.includes(tournamentTarget)) {
    shown.current.push(tournamentTarget);
  }
  if (shown.current.length === 0) {
    return null;
  }

  return (
    <div data-testid="demo-nav">
      {shown.current.map((chapter) => (
        <div
          key={chapter}
          data-testid={`demo-tournament-${chapter}`}
          data-component={COMPONENT_OF[chapter]}
          // Hidden, never unmounted (see the module header): the poll keeps polling and the
          // playhead keeps its position while the cursor is elsewhere.
          hidden={chapter !== target}
          style={chapter === target ? CHAPTER_SURFACE_STYLE : { display: 'none' }}
        >
          {renderChapter(chapter, seams)}
        </div>
      ))}
    </div>
  );
}
